// packages/web/src/lib/tenant.ts — A2.1b's stopgap tenant-selection.
//
// GAP, flagged rather than papered over: this codebase's server has no
// "list my tenant memberships" read (mapped and confirmed absent —
// packages/server/src/plugins/auth.ts only has POST /auth/tenants to
// CREATE one; nothing reads a caller's existing memberships back). D24's
// model sends X-Tenant-Id on every /api/* call, chosen client-side — but
// with no way to ask the server "which tenants am I in", this PWA has no
// server-backed way to discover the answer after a bare login.
//
// Stopgap for THIS chunk (a single-tenant demo, per the seedActorDemo.ts
// dev script): the tenant id is captured once, client-side only, via a
// `?tenant=<id>` query param on the very first load (the seed script
// prints a ready-to-use URL carrying it) and then persisted to
// localStorage for every later visit. If neither is present, the app asks
// once (screens/ConnectWorkspace.tsx) rather than silently failing.
//
// This is explicitly NOT a multi-tenant switcher — revisit once a real
// "list my tenants" endpoint exists; noted in the A2.1b report rather than
// inventing that endpoint here.
const TENANT_STORAGE_KEY = 'vowric_tenant_id';
const TENANT_VERTICAL_STORAGE_KEY = 'vowric_tenant_vertical';

export function getStoredTenantId(): string | null {
  try {
    return window.localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredTenantId(tenantId: string): void {
  try {
    window.localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
  } catch {
    // localStorage unavailable (private-browsing edge cases) — the
    // ConnectWorkspace screen simply re-asks next load; not fatal.
  }
}

export function clearStoredTenantId(): void {
  try {
    window.localStorage.removeItem(TENANT_STORAGE_KEY);
    window.localStorage.removeItem(TENANT_VERTICAL_STORAGE_KEY);
  } catch {
    // see setStoredTenantId
  }
}

// C1: the active workspace's VERTICAL, alongside its id.
//
// It already reaches this app — GET /auth/tenants returns it on every
// TenantMembership, and POST /auth/tenants is handed it at creation — but
// TenantResolver used to read it and throw it away, passing only the id up to
// App. The skin needs it (lib/skin.ts maps vertical → data-pack), so it is
// now persisted beside the id by the same two writers, in the same stopgap
// localStorage the id itself lives in.
//
// KNOWN GAP, flagged not papered over: the `?tenant=<id>` bootstrap path
// carries no vertical, and there is still no "read one tenant" endpoint to
// fill it in from. A workspace entered that way stays UNSKINNED until the
// next pass through TenantResolver. Unskinned is a real state (Actor ships in
// it), so the failure mode is a missing skin, never a wrong one.
export function getStoredTenantVertical(): string | null {
  try {
    return window.localStorage.getItem(TENANT_VERTICAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredTenantVertical(vertical: string): void {
  try {
    window.localStorage.setItem(TENANT_VERTICAL_STORAGE_KEY, vertical);
  } catch {
    // see setStoredTenantId
  }
}

/** Seeds localStorage from a `?tenant=<id>` query param, once, if present.
 * Safe to call unconditionally on every boot — a no-op once the param is
 * gone from the URL (main.tsx clears it after the first read). */
export function bootstrapTenantFromUrl(): void {
  const fromUrl = new URLSearchParams(window.location.search).get('tenant');
  if (fromUrl) setStoredTenantId(fromUrl);
}

/* ------------------------------------------------------------------ *
 * B18b — WHICH SHELL this workspace shows: the cockpit, or the client
 * portal.
 *
 * The authority is always the server (`GET /api/portal/me` answers only a
 * client-role membership). This is a remembered HINT so nobody waits on a
 * network round trip to find out which surface they are looking at:
 *
 *   - a returning client goes straight to their portal, with no flash of
 *     a cockpit they are not a member of;
 *   - Huan goes straight to the cockpit, with no "one moment" beat added
 *     to every cold load for a case that is not his.
 *
 * Per TENANT, because the same person can be a client in one workspace and
 * the owner of another. Wrong is self-correcting: the fetch that follows
 * overwrites it, and every route the wrong shell would call is gated
 * server-side anyway — a stale hint costs a redraw, never access.
 * ------------------------------------------------------------------ */
export type ShellKind = 'portal' | 'app';

const SHELL_HINT_PREFIX = 'vowric_shell_';

export function getShellHint(tenantId: string): ShellKind | null {
  try {
    const v = window.localStorage.getItem(`${SHELL_HINT_PREFIX}${tenantId}`);
    return v === 'portal' || v === 'app' ? v : null;
  } catch {
    return null;
  }
}

export function setShellHint(tenantId: string, kind: ShellKind): void {
  try {
    window.localStorage.setItem(`${SHELL_HINT_PREFIX}${tenantId}`, kind);
  } catch {
    // see setStoredTenantId — a hint that cannot be stored is only a hint.
  }
}
